import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, AlertCircle, Info, Send, GitBranch, Lock } from "lucide-react";
import { ppap } from "@/data/fixtures";
import useHashHighlight from "@/hooks/useHashHighlight";
import { usePersona, authorityLabels } from "@/context/PersonaContext";

const severityMap = {
  critical: {
    label: "Critical",
    icon: AlertOctagon,
    color: "var(--mos-red)",
    bg: "var(--mos-red-bg)",
    border: "var(--mos-red-border)",
  },
  high: {
    label: "High",
    icon: AlertCircle,
    color: "var(--mos-amber)",
    bg: "var(--mos-amber-bg)",
    border: "var(--mos-amber-border)",
  },
  medium: {
    label: "Medium",
    icon: Info,
    color: "#B45309",
    bg: "#FEF3C7",
    border: "#FDE68A",
  },
};

export default function PPAPReport() {
  const navigate = useNavigate();
  const percent = Math.round((ppap.elementsPresent / ppap.elementsRequired) * 100);
  useHashHighlight();
  const { hasAuthority } = usePersona();
  const canApprove = hasAuthority("quality-disposition");
  const gatedHelper = authorityLabels["quality-disposition"];

  // Cross-module link: d4 (Gauge R&R exceeds validity) ↔ Suppliers s1 (Castellan R&R lapsed).
  const crossLinks = {
    d4: {
      to: "/suppliers#s1",
      label: "See supplier posture · Castellan R&R lapsed",
      testId: "xlink-d4-to-suppliers",
    },
  };

  const onApproveSend = () => {
    toast.success("Correction request logged and queued · pending R. Diaz approval", {
      duration: 4200,
    });
  };

  return (
    <div data-testid="page-ppap" className="space-y-6">
      {/* Persistent banner */}
      <div
        className="rounded-md border px-4 py-2.5 text-[12.5px]"
        style={{
          background: "#F1F5F9",
          borderColor: "var(--mos-border)",
          color: "var(--mos-text-muted)",
        }}
        data-testid="ppap-banner"
      >
        <span className="font-medium text-slate-700">Note · </span>
        {ppap.banner}
      </div>

      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">
            Quality · PPAP Completeness Report
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            Package <span className="mos-mono">{ppap.id}</span> · {ppap.description}
          </h1>
          <div className="mt-2 text-[13.5px] text-slate-600">
            Supplier{" "}
            <span className="text-slate-900 font-medium">{ppap.supplier}</span>{" "}
            · Submitted{" "}
            <span className="mos-mono text-slate-900">{ppap.submitted}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Elements present
          </div>
          <div
            className="mos-mono text-[26px] font-semibold text-slate-900 leading-none"
            data-testid="ppap-elements"
          >
            {ppap.elementsPresent} <span className="text-slate-400">/</span>{" "}
            {ppap.elementsRequired}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ background: "#E5E7EB" }}
        >
          <div
            style={{
              width: `${percent}%`,
              background: "var(--mos-accent)",
              height: "100%",
            }}
            data-testid="ppap-progress"
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[12px] text-slate-500">
          <span>4 elements outstanding</span>
          <span className="mos-mono">{percent}%</span>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-3 gap-6 pt-2">
        {/* Defects list */}
        <div className="col-span-2 space-y-3.5" data-testid="ppap-defects">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
            Outstanding elements
          </div>
          {ppap.defects.map((d) => {
            const s = severityMap[d.severity];
            const Icon = s.icon;
            return (
              <div
                key={d.id}
                className="mos-surface rounded-md p-5 flex gap-4"
                data-testid={`defect-${d.id}`}
                data-record-id={d.id}
              >
                <span
                  className="h-8 w-8 rounded-full inline-flex items-center justify-center flex-none"
                  style={{ background: s.bg }}
                >
                  <Icon size={15} style={{ color: s.color }} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border"
                      style={{
                        color: s.color,
                        background: s.bg,
                        borderColor: s.border,
                      }}
                    >
                      {s.label}
                    </span>
                    <span className="text-[14.5px] font-medium text-slate-900">
                      {d.title}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[13.5px] text-slate-700 leading-relaxed">
                    {d.detail}
                  </p>
                  <button
                    type="button"
                    className="mt-2.5 text-[12.5px] text-[color:var(--mos-accent)] hover:underline cursor-pointer"
                    data-testid={`defect-source-${d.id}`}
                    onClick={(e) => e.preventDefault()}
                  >
                    {d.source}
                  </button>
                  {crossLinks[d.id] && (
                    <button
                      type="button"
                      onClick={() => navigate(crossLinks[d.id].to)}
                      className="mt-2 ml-4 inline-flex items-center gap-1.5 text-[12px] text-[color:var(--mos-accent)] hover:underline font-medium"
                      data-testid={crossLinks[d.id].testId}
                    >
                      <GitBranch size={12} />
                      {crossLinks[d.id].label} →
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Correction request panel */}
        <aside className="col-span-1" data-testid="correction-request">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">
            Supplier correction request
          </div>
          <div className="mos-surface rounded-md p-5">
            <div
              className="text-[11px] font-medium mos-mono px-2 py-1 rounded inline-block"
              style={{
                background: "var(--mos-amber-bg)",
                color: "var(--mos-amber)",
              }}
              data-testid="correction-status"
            >
              {ppap.correctionRequest.status}
            </div>
            <div
              className="mt-4 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-line rounded p-4 border"
              style={{
                background: "var(--mos-surface-2)",
                borderColor: "var(--mos-border)",
              }}
            >
              {ppap.correctionRequest.body}
            </div>
            <button
              type="button"
              onClick={onApproveSend}
              disabled={!canApprove}
              className={
                "mos-btn w-full justify-center mt-5 " +
                (canApprove
                  ? "mos-btn-primary"
                  : "mos-btn-secondary cursor-not-allowed opacity-60")
              }
              data-testid="btn-approve-send-correction"
              aria-disabled={!canApprove}
            >
              <Send size={14} />
              Approve and send
            </button>
            {!canApprove && (
              <div
                className="mt-2.5 rounded-md border px-3 py-2 text-[11.5px] flex items-center gap-2"
                style={{
                  background: "#FEF3C7",
                  borderColor: "var(--mos-amber-border)",
                  color: "#78350F",
                }}
                data-testid="ppap-gated-helper"
              >
                <Lock size={11} />
                {gatedHelper}
              </div>
            )}
            <p className="mt-3 text-[11.5px] text-slate-500 leading-relaxed">
              On approval, the draft is queued for R. Diaz to release to
              Castellan Metalworks. Written to audit trail.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
